/**
 * <s-sticker-drag>: lets shoppers "peel" a promo sticker and re-stick it anywhere
 * inside its positioned parent (the product's first image). Dragging only starts on
 * the sticker itself (touch-action: none), so page and gallery scrolling elsewhere is
 * untouched. The position is not saved; the sticker returns home on reload.
 */
class StickerDrag extends HTMLElement {
  #startX = 0;
  #startY = 0;
  #offsetX = 0;
  #offsetY = 0;
  #baseX = 0;
  #baseY = 0;
  #bounds = null;
  #pointerId = null;
  #reducedMotion = null;

  connectedCallback() {
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Set inline so dragging works even before the utility CSS is rebuilt.
    this.style.touchAction = 'none';
    this.style.userSelect = 'none';
    this.style.cursor = 'grab';
    this.addEventListener('pointerdown', this.#onDown);
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerup', this.#onUp);
    this.addEventListener('pointercancel', this.#onUp);
    this.addEventListener('lostpointercapture', this.#onUp);
    this.addEventListener('dragstart', this.#preventNative);
    this.addEventListener('click', this.#preventNative);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onDown);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerup', this.#onUp);
    this.removeEventListener('pointercancel', this.#onUp);
    this.removeEventListener('lostpointercapture', this.#onUp);
    this.removeEventListener('dragstart', this.#preventNative);
    this.removeEventListener('click', this.#preventNative);
  }

  #preventNative = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  #setTransition(value) {
    this.style.transition = this.#reducedMotion.matches ? 'none' : value;
  }

  #onDown = (event) => {
    if (event.button !== 0 || this.#pointerId !== null) return;
    const parent = this.offsetParent;
    if (!parent) return;

    event.preventDefault();
    this.#pointerId = event.pointerId;
    this.setPointerCapture(event.pointerId);

    const parentRect = parent.getBoundingClientRect();
    const rect = this.getBoundingClientRect();
    // Allowed travel, measured from the sticker's untranslated home position.
    this.#baseX = rect.left - parentRect.left - this.#offsetX;
    this.#baseY = rect.top - parentRect.top - this.#offsetY;
    this.#bounds = {
      minX: -this.#baseX,
      maxX: parentRect.width - rect.width - this.#baseX,
      minY: -this.#baseY,
      maxY: parentRect.height - rect.height - this.#baseY,
    };
    this.#startX = event.clientX - this.#offsetX;
    this.#startY = event.clientY - this.#offsetY;

    // Peel: lift off the page with a bigger shadow and a little tilt.
    this.#setTransition('scale 160ms ease-out, rotate 160ms ease-out, filter 160ms ease-out');
    this.style.scale = '1.12';
    this.style.rotate = '-4deg';
    this.style.filter = 'drop-shadow(0 10px 12px rgb(0 0 0 / 0.28))';
    this.dataset.dragging = '';
    this.style.cursor = 'grabbing';
  };

  #onMove = (event) => {
    if (event.pointerId !== this.#pointerId || !this.#bounds) return;
    const { minX, maxX, minY, maxY } = this.#bounds;
    this.#offsetX = Math.min(maxX, Math.max(minX, event.clientX - this.#startX));
    this.#offsetY = Math.min(maxY, Math.max(minY, event.clientY - this.#startY));
    this.style.translate = `${this.#offsetX}px ${this.#offsetY}px`;
  };

  #onUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    this.#pointerId = null;
    this.#bounds = null;

    // Re-stick: press back down with a small springy settle.
    this.#setTransition(
      'scale 280ms cubic-bezier(0.34, 1.56, 0.64, 1), rotate 280ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 200ms ease-out'
    );
    this.style.scale = '';
    this.style.rotate = '';
    this.style.filter = '';
    delete this.dataset.dragging;
    this.style.cursor = 'grab';
  };
}

if (!customElements.get('s-sticker-drag')) {
  customElements.define('s-sticker-drag', StickerDrag);
}
